import { getDecryptedData, getEncryptedText, getEncryptedTextV2 } from "../../utils/common";

export const API = `${process.env.REACT_APP_API_URL}/api/v8`;
export const LOCAL_API = `http://localhost:8080/api/v8`;
// export const API = `http://192.168.1.5:8080/api/v8`;

const POST = "post";
const GET = "get";
const PUT = "put";
const PATCH = "patch";
const DELETE = "delete";

const INVALID_TOKEN = "invalid or expired jwt";
const HEALTH_SECRET = process.env.REACT_APP_HEALTH_SECRET_KEY || '';
// Skips the ngrok browser-warning interstitial (ERR_NGROK_6024) that free tunnels (e.g. the uat env) serve instead of the API response.
const NGROK_SKIP_HEADER = { "ngrok-skip-browser-warning": "true" };

const getToken = () => {
  const token = localStorage.getItem("token");
  if (token !== null) {
    // token previously stored
    return token;
  }
  return "";
};

const parseErrorBody = async (response) => {
  const status = response.status;
  const contentType = response.headers.get("Content-Type") || "";
  try {
    if (contentType.indexOf("application/json") !== -1) {
      const json = await response.json();
      return {
        ...json,
        status,
        message: json?.message || `Request failed (${status})`,
      };
    }
    const text = await response.text();
    const isHtml =
      typeof text === "string" &&
      (text.trimStart().startsWith("<!DOCTYPE") ||
        text.trimStart().startsWith("<html"));
    return {
      status,
      message: isHtml
        ? status === 404
          ? "Resource not found"
          : `Request failed (${status})`
        : text || `Request failed (${status})`,
    };
  } catch {
    return {
      status,
      message:
        status === 404
          ? "Resource not found"
          : `Request failed (${status || "network"})`,
    };
  }
};

const handleResponse = async (response) => {
  if (!response.ok) {
    handleTokenError(response);
    return Promise.reject(await parseErrorBody(response));
  }
  const contentType = response.headers.get("Content-Type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return response.json();
  } else {
    return response.text();
  }
};

const Request = async (
  route,
  method,
  priv = true,
  payload,
  imageType = "",
  API_URL = ""
) => {
  let config = {
    method: method,
    headers: { ...NGROK_SKIP_HEADER },
  };
  if (["post", "put", "patch"].includes(method)) {
    config = {
      ...config,
      body: JSON.stringify(payload),
      headers: {
        ...NGROK_SKIP_HEADER,
        "content-type": "text/plain",
        health_secret: HEALTH_SECRET,
      },
    };
  }
  if (priv) {
    let token = getToken();
    config.headers = {
      ...config.headers,
      Token: `${token}`,
      health_secret: HEALTH_SECRET,
    };
  }
  let api_temp = API;
  // if (API_URL && API_URL !== "") {
  // 	api_temp = API_URL;
  // }
  return fetch(`${api_temp}${route}`, config)
    .then(async (res) => {
      return await handleResponse(res);
    })
    .catch((err) => {
      throw handleTokenError(err);
    });
};

const LocalRequest = async (
  route,
  method,
  priv = true,
  payload,
  imageType = "",
  API_URL = ""
) => {
  let config = {
    method: method,
    headers: { ...NGROK_SKIP_HEADER },
  };
  if (["post", "put", "patch"].includes(method)) {
    config = {
      ...config,
      body: JSON.stringify(payload),
      headers: {
        ...NGROK_SKIP_HEADER,
        "content-type": "text/plain",
        health_secret: HEALTH_SECRET,
      },
    };
  }
  if (priv) {
    let token = getToken();
    config.headers = {
      ...config.headers,
      Token: `${token}`,
      health_secret: HEALTH_SECRET,
    };
  }
  let api_temp = LOCAL_API;
  // if (API_URL && API_URL !== "") {
  // 	api_temp = API_URL;
  // }
  return fetch(`${api_temp}${route}`, config)
    .then((res) => {
      return handleResponse(res);
    })
    .catch((err) => {
      throw handleTokenError(err);
    });
};

const handleTokenError = (err) => {
  if (
    err?.status == 401 ||
    err?.message == INVALID_TOKEN ||
    err?.statusText == INVALID_TOKEN
  ) {
    console.log(err);
  }
  return err;
};

// New API for encrypted request and decrypted response
export const handleEncryptedResponse = async (response) => {
  const contentType = response.headers.get("Content-Type") || "";

  if (contentType) {
    let resp = null;
    if (contentType.indexOf("application/json") !== -1) {
      resp = await response.json();
    } else {
      // text/plain, text/html, or bare encrypted ciphertext
      resp = await response.text();
    }

    const parsed = await JSON.parse(getDecryptedData(resp));

    if (Number(parsed?.code) == 1) {
      return parsed;
    } else {
      return parsed;
    }
  } else {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(getDecryptedData(text));
    } catch {
      return text;
    }
  }
};

const encryptedRequest = async (
  route,
  {
    baseURL = API,
    method = GET,
    payload = null,
    formData = null,
    headers = {},
    json = true,
    priv = true,
    content_type = null,
    encrypted = true,
    withoutBody = false,
  }
) => {
  //   const activeVersion = DeviceInfo.getVersion() // return "0.0.2"
  //   const activeBuildNumber = DeviceInfo.getBuildNumber() // return 2
  //   const appVersion = `${Platform.OS} ${activeVersion}-${activeBuildNumber}`

  let init = {
    method: method,
    headers: {
      ...NGROK_SKIP_HEADER,
      "content-type": "text/plain",
      health_secret: HEALTH_SECRET,
      ...headers,
    },
    ...(withoutBody ? {} : {body: ""}),
  };

  //   const { Auth } = Store.getState()

  if (formData) {
    init = {
      ...init,
      body: encrypted ? getEncryptedTextV2(formData) : formData,
    }
  }

  if (payload) {
    init = {
      ...init,
      headers: {
        ...init.headers,
      },
      body: encrypted ? getEncryptedTextV2(payload) : JSON.stringify(payload),
    };
  }
  if (content_type) init.headers["Content-Type"] = content_type;

  if (priv) {
    const authToken = getToken();

    if (authToken) {
      init.headers = {
        ...init.headers,
        Token: `${authToken}`,
      };
    }
  }

  return fetch(`${baseURL}${route}`, init)
    .then(async (res) => {
      if (res.status === 401) {
        HandleError(401, "Unauthorized");
      }

      if (!json) {
        return res;
      }

      res = await handleEncryptedResponse(res);
      return res;
    })
    .catch(async (error) => {
      if (error.message === "Unauthorized") {
        HandleError(401, "Unauthorized");
      }
      throw error;
    });
};

const fileUploadRequest = async (route, formData, { baseURL = API, priv = true } = {}) => {
  const headers = {
    ...NGROK_SKIP_HEADER,
    health_secret: HEALTH_SECRET,
  };

  if (priv) {
    const authToken = getToken();
    if (authToken) {
      headers.Token = `${authToken}`;
    }
  }

  return fetch(`${baseURL}${route}`, {
    method: POST,
    body: formData,
    headers,
  }).then(async (res) => handleEncryptedResponse(res));
};

const HandleError = (error, msg) => {
  console.log("error", error);
  let errMsg = "";
  if (error) {
    switch (error.status) {
      case 400:
        errMsg = msg.INVALID_DATA_ERROR;
        break;
      case 401:
        errMsg = msg.UNAUTHORISED;
        break;
      case 403:
        errMsg = msg.FORBIDDEN_ERROR;
        break;
      case 404:
        errMsg = msg.NOT_FOUND;
        break;
      case 409:
        errMsg = msg.CONFLICT;
        break;
      case 413:
        errMsg = msg.PAYLOAD_TOO_LARGE;
        break;
      case 500:
        errMsg = msg.SERVER_ERROR;
        break;
      default:
        errMsg = msg.DEFAULT_ERROR;
    }
  }
  console.log("errMsg", error);
  return errMsg;
};

let base = {
  API,
  POST,
  GET,
  PUT,
  PATCH,
  DELETE,
  Request,
  HandleError,
  LocalRequest,
  encryptedRequest,
  fileUploadRequest,
};

export default base;
